// Phone input with US-defaulted country code and lightweight format validation.
// Stores value as { country_code: "+1", national_number: "5551234567" }.
// Shows a friendly +1 flag badge and a 10-digit number field, and surfaces a
// red validation message whenever the user types non-digits or leaves fewer
// than 10 digits — matches UAT Feb-2026 requirement.
import { useEffect, useState } from "react";

export function isValidUSPhone(national) {
  const digits = String(national || "").replace(/\D+/g, "");
  return digits.length === 10;
}

function formatUSPhone(digits) {
  const d = String(digits || "").replace(/\D+/g, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

export default function PhoneField({
  value,                 // { country_code, national_number } OR legacy string
  onChange,
  disabled = false,
  testid = "portal-phone",
  errorTestid,
}) {
  const cc = value?.country_code || "+1";
  const persisted = typeof value === "string" ? value : (value?.national_number || "");

  // Track exactly what the user typed so we can surface a message when they
  // enter non-digit garbage (e.g. "abc") — otherwise the sanitizer would just
  // silently strip everything and no error would appear.
  const [raw, setRaw] = useState(() => formatUSPhone(persisted));
  const [dirty, setDirty] = useState(false);

  // Re-sync when parent hands us a new persisted value (e.g. after Save Draft
  // reload) but the user hasn't started typing.
  useEffect(() => {
    if (!dirty) setRaw(formatUSPhone(persisted));
  }, [persisted, dirty]);

  const digits = raw.replace(/\D+/g, "").slice(0, 10);
  const containedGarbage = raw.trim() !== "" && digits.length === 0 && !/^[\d\s\-()+]*$/.test(raw);
  const partial = digits.length > 0 && digits.length < 10;
  const complete = digits.length === 10;
  const valid = complete || (digits.length === 0 && !containedGarbage);

  const handle = (v) => {
    setDirty(true);
    setRaw(v);
    const d = v.replace(/\D+/g, "").slice(0, 10);
    // Persist immediately so parent can compute progress / disable Save Draft.
    // We send whatever digits we have — parent treats partial as invalid via
    // isValidUSPhone(). Empty digits clear the number.
    onChange?.({ country_code: cc, national_number: d });
  };

  const errorMessage = containedGarbage
    ? "Phone number must contain only digits."
    : (partial ? `Enter all 10 digits (${digits.length}/10 so far).` : "");

  return (
    <div>
      <div className={`flex items-stretch rounded-lg border ${valid ? "border-slate-200" : "border-red-300"} bg-white overflow-hidden focus-within:ring-2 focus-within:ring-sky-500/30 focus-within:border-sky-500 transition ${disabled ? "bg-slate-50" : ""}`}>
        <span
          className="px-3 flex items-center gap-1 bg-slate-50 border-r border-slate-200 text-sm text-slate-600 select-none"
          data-testid={`${testid}-cc`}
          title="US country code (+1)"
        >
          <span className="text-base leading-none">🇺🇸</span>
          <span className="font-medium">{cc}</span>
        </span>
        <input
          type="tel"
          value={raw}
          onChange={(e) => handle(e.target.value)}
          onBlur={() => setRaw(formatUSPhone(raw))}
          disabled={disabled}
          inputMode="tel"
          autoComplete="tel-national"
          placeholder="555-123-4567"
          className="flex-1 px-3 py-2 text-sm outline-none bg-transparent disabled:text-slate-500"
          data-testid={testid}
          aria-invalid={!valid}
        />
      </div>
      {!valid && (
        <p className="text-xs text-red-600 mt-1" data-testid={errorTestid || `${testid}-error`}>
          {errorMessage || "Enter a valid 10-digit US phone number."}
        </p>
      )}
    </div>
  );
}
